import { Box, Switch, Typography } from "@mui/material";
import { FaInfoCircle } from "react-icons/fa";
import React, { useContext, useEffect, useState } from "react";
import TaxWithConvertedValue from "../TaxWithConvertedValue";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import {
  typeValueOptions,
  cortesyOptions,
  NFMMOptions,
  typeTax,
  DefaultTax,
  TaxData,
} from "../../../../types/budget/service-item";
import {
  BudgetWideAppliedTaxes,
  editBitributedRoles,
  ExchangeData,
} from "../../../../types/budget/budget";
import { AuthContext } from "../../../../contexts/auth";

const valueTypeOptionsDefault = createOptionsFromEnum(typeValueOptions);
const percentageOnlyOptions = valueTypeOptionsDefault.filter(
  (option: any) => option.value === "PERCENTAGE"
);
const valueCortesyOptions = createOptionsFromEnum(cortesyOptions);
const typeTaxOptions = createOptionsFromEnum(typeTax);
const nfMMOptions = createOptionsFromEnum(NFMMOptions);

type Props = {
  budgetWideAppliedTaxes: BudgetWideAppliedTaxes;
  taxData: TaxData | undefined;
  compareTaxData?: TaxData;
  exchange: ExchangeData;
  compareExchange: ExchangeData;
  onFieldBlur: (value: TaxData) => void;
};

function chunkArray(arr: any[], size: number) {
  return arr.reduce((acc: any[], _, i) => {
    if (i % size === 0) acc.push(arr.slice(i, i + size));
    return acc;
  }, []);
}

const BaseValues: React.FC<Props> = ({
  taxData,
  compareTaxData,
  exchange,
  compareExchange,
  onFieldBlur,
}: Props) => {
  const [localTaxData, setLocalTaxData] = useState<TaxData | undefined>(
    taxData
  );
  const [enableEdit, setEnableEdit] = useState(false);
  const { userRoleName } = useContext(AuthContext);
  const canEditBiTributed = editBitributedRoles.includes(userRoleName);

  useEffect(() => {
    setLocalTaxData(taxData);
  }, [taxData]);

  const handleCalcValues = (
    type: string,
    taxParameters: { value: number; typeTax: string }
  ) => {
    const currState: TaxData = JSON.parse(JSON.stringify(localTaxData));
    switch (type) {
      case "SERVICE":
        currState.serviceTax.value = taxParameters?.value || 0;
        currState.serviceTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "ISS":
        currState.iss.value = taxParameters?.value || 0;
        currState.iss.typeTax = taxParameters?.typeTax || "";
        break;
      case "CBS":
        currState.cbs = {
          ...(currState.cbs || {
            currencyValue: 0,
            typeTax: "PERCENTAGE",
            value: 0,
            valueBRL: 0,
          }),
          value: taxParameters?.value || 0,
          typeTax: taxParameters?.typeTax || "PERCENTAGE",
        };
        break;
      case "IBS":
        currState.ibs = {
          ...(currState.ibs || {
            currencyValue: 0,
            typeTax: "PERCENTAGE",
            value: 0,
            valueBRL: 0,
          }),
          value: taxParameters?.value || 0,
          typeTax: taxParameters?.typeTax || "PERCENTAGE",
        };
        break;
      case "TARIFF":
        currState.tariff.value = taxParameters?.value || 0;
        currState.tariff.typeTax = taxParameters?.typeTax || "";
        break;
      case "LOCAL_TAX":
        currState.localTax.value = taxParameters?.value || 0;
        currState.localTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "TOURISM_TAX":
        currState.tourismTax.value = taxParameters?.value || 0;
        currState.tourismTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "FEE":
        currState.fee.value = taxParameters?.value || 0;
        currState.fee.typeTax = taxParameters?.typeTax || "";
        break;
      case "EXTRAS":
        currState.extras.value = taxParameters?.value || 0;
        currState.extras.typeTax = taxParameters?.typeTax || "";
        break;
      case "IOF":
        currState.iof.value = taxParameters?.value || 0;
        currState.iof.typeTax = taxParameters?.typeTax || "";
        break;
      case "MARKUP":
        currState.markup.value = taxParameters?.value || 0;
        currState.markup.typeTax = taxParameters?.typeTax || "";
        break;
      case "SUPPLIER_TRIBUTE":
        currState.supplier_tribute = {
          ...(currState.supplier_tribute || {
            currencyValue: 0,
            typeTax: "PERCENTAGE",
            value: 0,
            valueBRL: 0,
          }),
          value: taxParameters?.value || 0,
          typeTax: taxParameters?.typeTax || "PERCENTAGE",
        };
        break;
      case "OVER":
        currState.overhead.value = taxParameters?.value || 0;
        currState.overhead.typeTax = taxParameters?.typeTax || "";
        break;
      case "COMMISSION":
        currState.commission.value = taxParameters?.value || 0;
        currState.commission.typeTax = taxParameters?.typeTax || "";
        break;
      case "INCENTIVE":
        currState.incentive.value = taxParameters?.value || 0;
        currState.incentive.typeTax = taxParameters?.typeTax || "";
        break;
      case "TRIBUTE":
        currState.tribute.value = taxParameters?.value || 0;
        currState.tribute.typeTax = taxParameters?.typeTax || "";
        break;
      case "DOUBLE_CHARGED":
        currState.doubleCharged.value = taxParameters?.value || 0;
        currState.doubleCharged.typeTax = taxParameters?.typeTax || "";
        break;
      default:
        break;
    }

    setLocalTaxData(currState);
    onFieldBlur(currState);
  };
  function definePreviousValue(
    type: keyof typeof typeTax,
    referenceData: TaxData | undefined
  ) {
    switch (type) {
      case "SERVICE":
        return referenceData?.serviceTax;
      case "EXTRAS":
        return referenceData?.extras;
      case "ISS":
        return referenceData?.iss;
      case "CBS":
        return (
          referenceData?.cbs || {
            typeTax: "PERCENTAGE",
            value: 0,
            currencyValue: 0,
            valueBRL: 0,
          }
        );
      case "IBS":
        return (
          referenceData?.ibs || {
            typeTax: "PERCENTAGE",
            value: 0,
            currencyValue: 0,
            valueBRL: 0,
          }
        );
      case "TARIFF":
        return referenceData?.tariff;
      case "LOCAL_TAX":
        return referenceData?.localTax;
      case "TOURISM_TAX":
        return referenceData?.tourismTax;
      case "FEE":
        return referenceData?.fee;
      case "IOF":
        return referenceData?.iof;
      case "MARKUP":
        return referenceData?.markup;
      case "SUPPLIER_TRIBUTE":
        return (
          referenceData?.supplier_tribute || {
            typeTax: "PERCENTAGE",
            value: 0,
            currencyValue: 0,
            valueBRL: 0,
          }
        );
      case "OVER":
        return referenceData?.overhead;
      case "COMMISSION":
        return referenceData?.commission;
      case "INCENTIVE":
        return referenceData?.incentive;
      case "TRIBUTE":
        return referenceData?.tribute;
      case "DOUBLE_CHARGED":
        return referenceData?.doubleCharged;
      default:
        return {
          typeTax: "",
          value: 0,
          currencyValue: 0,
          valueBRL: 0,
        } as DefaultTax;
    }
  }

  return (
    <Box>
      {/* Edit warning banner with enable switch */}
      <Box
        sx={{
          width: "95%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#E6F0FB",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, width: "75%" }}
        >
          <FaInfoCircle color="#1976d2" size={14} />
          <Typography sx={{ color: "#333", fontSize: 14 }}>
            Editar taxas e tarifas desta seção substituirá as respectivas taxas
            em todos os serviços. Prossiga com cautela.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 14, color: "#333" }}>
            Habilitar edição
          </Typography>
          <Switch
            checked={enableEdit}
            onChange={(e) => setEnableEdit(e.target.checked)}
            color="primary"
          />
        </Box>
      </Box>
      {chunkArray(
        typeTaxOptions.filter((el: any) => {
          if (["BRL", ""].includes(exchange?.currency) && el.value === "IOF")
            return false;
          if (!["BRL"].includes(exchange?.currency) && el.value === "ISS")
            return false;
          if (el.value === "DOUBLE_CHARGED" && !canEditBiTributed) return false;
          if (el.value === "TARIFF") return false;
          if (el.value === "OVER") return false;
          return true;
        }),
        4
      ).map((group: any[], groupIndex: number) => (
        <Box
          key={groupIndex}
          sx={{
            display: "flex",
            flexDirection: "row",
            mb: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {group.map((el: any, index: number) => {
            const calcOption =
              el.value === "TARIFF"
                ? valueCortesyOptions
                : el.value === "NFMM"
                ? nfMMOptions
                : el.value === "SUPPLIER_TRIBUTE" ||
                  el.value === "CBS" ||
                  el.value === "IBS"
                ? percentageOnlyOptions
                : valueTypeOptionsDefault;

            return (
              <Box
                key={index}
                sx={{ flex: 1, mr: index !== group.length - 1 ? 2 : 0 }}
              >
                <TaxWithConvertedValue
                  type={el.value}
                  title={el.label}
                  previousState={definePreviousValue(el.value, localTaxData)}
                  calculusOptions={calcOption}
                  triggerCalculus={handleCalcValues}
                  disableChanges={!enableEdit}
                  selectedCurrencyCode={exchange.currency}
                  comparisonCurrencyCode={compareExchange.currency}
                  comparisonState={definePreviousValue(
                    el.value,
                    compareTaxData
                  )}
                  hasTotal={false}
                />
              </Box>
            );
          })}

          {Array.from({ length: 4 - group.length }).map((_, fillerIndex) => (
            <Box
              key={`filler-${fillerIndex}`}
              sx={{
                flex: 1.03,
                mr: fillerIndex !== 4 - group.length - 1 ? 2 : 0,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default BaseValues;
